/**
 * HeaderAndSectionItem.js
 * Created by calhosh on 2/4/2022.
 */

import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {IconoirCustomMoreVerticalAlt} from '@ey-xd/motif-icon';
import EllipseControl from '../EllipsesControl/EllipsesControl';
import {labels} from '../../../util/utils';
import env from '../../../util/env';
import {clientSideEventNames, entities, serviceURL} from '../../../util/uiconstants';
import ValidationControl from '../CanvasForm/Common/ValidationControl';
import {deleteHeader} from '../../../actions/FormData/formdataactions';
import ConfirmationModal from '../Modal/Modal';
import AddEditCustomModal from '../../Main/Forms/AddEditCustom/AddEditCustomModal';
import HeaderAndSectionItemSecondLevel from '../Rail/HeaderAndSectionItemSecondLevel';
import MotifDropdown from '@ey/motif-react-wrapper/Dropdown';
import DropdownItem from '../BaseComponentWrappers/MotifDropdownItem';
import MotifButton from '@ey/motif-react-wrapper/Button';
import {useDocumentId, useEngagementId} from '../../../util/customHooks';
import MotifIcon from '@ey/motif-react-wrapper/Icon';

function HeaderAndSectionItem({
	showValidationsIcon,
	currentSectionId,
	clearComments,
	railData,
	secondLevelDisplayedData,
	currentHeaderId,
	onClickHandle,
	formData,
	signOffs,
	isExpanded
}) {
	const [isOpen, setIsOpen] = useState(false),
		[showEditModal, setShowEditModal] = useState(false),
		[showDeleteModal, setShowDeleteModal] = useState(false),
		[header, setHeader] = useState({}),
		[modalType, setModalType] = useState(undefined),
		[isHovering, setIsHovering] = useState(false);

	const engagementId = useEngagementId(),
		documentId = useDocumentId();
	const dispatch = useDispatch();

	const getRailName = (label, customLabel, customCharacter = undefined) => {
		switch (customCharacter) {
			case '()':
				return `${label} (${customLabel})`;
			case '[]':
				return `${label} [${customLabel}]`;
			case undefined:
				return `${label} - ${customLabel}`;
			default:
				return `${label} ${customCharacter} ${customLabel}`;
		}
	};

	const handleClickOutside = () => {
		setIsOpen(false);
		setIsHovering(false);
	};

	const cancel = () => {
		switch (modalType) {
			case labels.delete:
				return setShowDeleteModal(false);
			case labels.edit:
				return setShowEditModal(false);
		}
	};

	const handleClickDropdownItem = (textContent) => {
		setModalType(textContent);
		setIsOpen(false);
		setIsHovering(false);
		switch (textContent) {
			case labels.edit:
				setShowEditModal(!showEditModal);
				break;
			case labels.delete:
				setShowDeleteModal(!showDeleteModal);
				break;
		}
	};

	const confirmActionClick = () => {
		dispatch(
			deleteHeader(env.getBaseURL(serviceURL, engagementId) + `forms/${documentId}/headers/${header?.id}`, {
				documentId: documentId,
				headerId: header?.id
			})
		)
			.then(() => {
				setHeader(undefined);
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onCreateOrEdit, {
						detail: {signOffs: signOffs, engagement: {engagementId: engagementId}}
					})
				);
			})
			.catch(() => {
				setShowDeleteModal(false);
			});
	};

	return (
		<>
			<>
				{(railData.isCustom || (secondLevelDisplayedData && secondLevelDisplayedData.length > 0)) && (
					<li
						className={`canvasformHeader ${currentHeaderId === railData.id ? 'activeHeader' : ''}`}
						data-id={railData.id}
					>
						<section
							id={`canvasformHeaderWrapper_${railData.id}`}
							className="canvasformHeaderWrapper"
							onMouseEnter={() => {
								setIsHovering(true);
							}}
							onMouseLeave={() => {
								if (!isOpen) {
									setIsHovering(false);
								}
							}}
						>
							<section
								data-id={railData.id}
								className="canvasformHeaderIcon" //check for currentSectionId === railData.id to set isActive class
								title={railData.customLabel ? getRailName(railData.name, railData.customLabel) : railData.name}
								onClick={onClickHandle}
							/>
							<span data-id={railData.id} className="canvasformHeaderText" onClick={onClickHandle}>
								<EllipseControl
									content={railData.customLabel ? getRailName(railData.name, railData.customLabel) : railData.name}
									tooltip={railData.customLabel ? getRailName(railData.name, railData.customLabel) : railData.name}
									displayId={''}
									noOfLines={2}
								/>
							</span>
							<span className="canvasformCustomHeader custom-labels">
								{railData.isCustom && isHovering && (
									<MotifDropdown
										open={isOpen}
										handleClickOutside={handleClickOutside}
										usePortal
										areaLabelledBy={`dropdown-trigger-${railData.id}`}
										handleOnOpen={() => setIsOpen(true)}
										handleOnClose={() => {
											setIsOpen(false);
											setIsHovering(false);
										}}
										trigger={
											<MotifButton
												id={`dropdown-trigger-${railData.id}`}
												variant="text"
												type="button"
												onClick={(e) => {
													setIsOpen(!isOpen);
													e.stopPropagation();
												}}
											>
												<MotifIcon iconFunction={IconoirCustomMoreVerticalAlt} />
											</MotifButton>
										}
										children={
											<>
												<DropdownItem
													onClick={() => {
														handleClickDropdownItem(labels.edit);
														setHeader(railData);
													}}
													children={labels.edit}
												/>
												<DropdownItem
													onClick={() => {
														handleClickDropdownItem(labels.delete);
														setHeader(railData);
													}}
													children={labels.delete}
												/>
											</>
										}
									/>
								)}
							</span>
							{showValidationsIcon &&
								(railData.incompleteCount ?? 0) +
									(railData.unresolvedCount ?? 0) +
									(railData.itFlowValidationCount ?? 0) >
									0 && (
									<span className="header-validationCount">
										<ValidationControl
											documentId={documentId}
											incompleteResponseCount={railData.incompleteCount ?? 0}
											unresolvedCommentCount={railData.unresolvedCount ?? 0}
											hideIcon
											railExpanded={isExpanded}
											darkBg
											clearComments={clearComments}
											entityUId={railData.id}
											entityId={entities.FormHeader}
											itFlowValidationCount={railData?.itFlowValidationCount || 0}
											itFlowFormValidations={railData?.itFlowValidations || []}
										/>
									</span>
								)}
						</section>
						{railData.secondLevel && railData.secondLevel.length > 0 ? (
							railData.secondLevel.map((rData) => {
								return (
									<HeaderAndSectionItemSecondLevel
										key={rData.id}
										clearComments={clearComments}
										railData={rData}
										currentSectionId={currentSectionId}
										getRailName={getRailName}
										onClickHandle={onClickHandle}
										showValidationsIcon={showValidationsIcon}
										isExpanded={isExpanded}
									/>
								);
							})
						) : (
							<section className="emptyContainer">{labels.noSectionsAvailable}</section>
						)}
					</li>
				)}
			</>
			{showDeleteModal && (
				<ConfirmationModal
					show={showDeleteModal}
					showButtons
					title={labels.deleteHeader}
					confirmBtnTitle={labels.delete}
					cancelBtnTitle={labels.cancelLabel}
					onOkClick={confirmActionClick}
					onHide={cancel}
					closeTitle={labels.closeLabel}
					modalsize="small"
				>
					<p>{labels.deleteHeaderTitle}</p>
					<p>{header?.name}</p>
				</ConfirmationModal>
			)}
			{showEditModal && (
				<>
					<AddEditCustomModal
						afterSave={() => {
							setShowEditModal(false);
							window.dispatchEvent(
								new CustomEvent(clientSideEventNames.onCreateOrEdit, {
									detail: {selectedId: currentHeaderId, signOffs: signOffs, engagement: {engagementId: engagementId}}
								})
							);
							window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
						}}
						formData={formData}
						entityParentId={header?.id}
						documentId={documentId}
						engagementId={engagementId}
						entity={'header'}
						entityId={header?.id}
						entityValue={header?.name}
						isEdit
						isCustom
						onHide={cancel}
						showTabs={false}
					/>
				</>
			)}
		</>
	);
}
export default React.memo(HeaderAndSectionItem);
